/**
 * DeleteAccountSheet — two-step destructive confirmation (M72 Phase 3).
 *
 * Structure mirrors the destructive-confirm dialog pattern at ProfilePage.tsx:355-396,
 * extended to a two-step flow per the M72 spec (lines 116-120):
 *
 * Step 1 (data-step="1"): Summary of what will be deleted + Cancel + "I understand, Delete" button.
 *   Header: ember-red/danger "DELETE ACCOUNT" (spec line 116).
 *
 * Step 2 (data-step="2"): Controlled text input gated to exact string "DELETE" (case-sensitive).
 *   Delete button is disabled until input === 'DELETE'.
 *   Confirm calls onConfirm prop.
 *
 * data-step is a STATIC literal reflecting the current step — not derived from config.
 *
 * Error display: red error-text pattern mirroring ProfilePage.tsx:417-427 (notificationError).
 *
 * Sheet pattern: fixed bottom-0 overlay, mirroring SnoozeSheet.tsx.
 */

import { useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/common/Button';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DeleteAccountSheetProps {
  isDeleting: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Exact string the user must type to enable the final delete button (case-sensitive). */
const CONFIRM_STRING = 'DELETE';

// ── Component ─────────────────────────────────────────────────────────────────

export function DeleteAccountSheet({
  isDeleting,
  error,
  onConfirm,
  onClose,
}: DeleteAccountSheetProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [confirmInput, setConfirmInput] = useState('');

  const isConfirmEnabled = confirmInput === CONFIRM_STRING;

  function handleAdvanceToStep2() {
    setStep(2);
  }

  function handleBack() {
    setStep(1);
    setConfirmInput('');
  }

  function handleClose() {
    setStep(1);
    setConfirmInput('');
    onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
        data-testid="delete-account-backdrop"
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-elevated)] border-t border-[var(--color-border)] rounded-t-2xl shadow-card-lg p-6 flex flex-col gap-5 max-h-[80vh] overflow-y-auto"
        data-testid="delete-account-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-account-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <h2
            id="delete-account-title"
            className="font-display text-display-sm text-red-400"
          >
            DELETE ACCOUNT
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close delete account dialog"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step 1: Summary of consequences */}
        {step === 1 && (
          <div
            data-step="1"
            data-testid="delete-account-step-1"
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-2">
              <p className="text-body-sm text-[var(--color-text-primary)] font-medium">
                This will permanently delete:
              </p>
              <ul className="flex flex-col gap-1 text-body-sm text-[var(--color-text-secondary)]">
                <li>Your profile and account credentials</li>
                <li>All properties, locations, and maps</li>
                <li>All seasons, tasks, and checklists</li>
                <li>All scouting logs and field notes</li>
                <li>All hunt records and harvest data</li>
                <li>All photos in the cloud</li>
              </ul>
              <p className="text-body-xs text-[var(--color-text-muted)] mt-1">
                This action cannot be undone. Local device data is also wiped.
              </p>
            </div>

            {/* Error inline — mirrors ProfilePage.tsx:417-427 notification error pattern */}
            {error && (
              <p
                className="text-body-xs text-red-500 bg-red-500/10 rounded px-3 py-2"
                data-testid="delete-account-error"
              >
                {error}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleAdvanceToStep2}
                disabled={isDeleting}
                data-testid="delete-account-step1-advance"
              >
                I understand, Delete
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Typed confirmation gate */}
        {step === 2 && (
          <div
            data-step="2"
            data-testid="delete-account-step-2"
            className="flex flex-col gap-4"
          >
            <div>
              <p className="text-body-sm text-[var(--color-text-primary)] mb-3">
                To confirm, type{' '}
                <span className="font-mono font-bold text-red-400">DELETE</span>{' '}
                in the field below:
              </p>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-label="Type DELETE to confirm account deletion"
                data-testid="delete-account-confirm-input"
                className="w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded text-[var(--color-text-primary)] text-body-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors duration-100 placeholder:text-[var(--color-text-muted)]"
              />
            </div>

            {/* Error inline — mirrors ProfilePage.tsx:417-427 notification error pattern */}
            {error && (
              <p
                className="text-body-xs text-red-500 bg-red-500/10 rounded px-3 py-2"
                data-testid="delete-account-error"
              >
                {error}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                disabled={isDeleting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={onConfirm}
                disabled={!isConfirmEnabled || isDeleting}
                data-testid="delete-account-confirm-button"
              >
                {isDeleting ? 'Deleting…' : 'Delete My Account'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
